import React, { useEffect, useState } from 'react';
import { isNumber } from 'lodash';
// @ts-ignore
import styles from './FilledQuestionnaireList.module.scss';
import FlatList from '../../../Base/Components/FlatList';
import useQuestionnaireResults from '../../Hooks/useQuestionnaireResults';
import FilledQuestionnaireItem from './FilledQuestionnaireItem';
import Checkbox from '../../../../Components/Base/Checkbox/Checkbox';
import ScrollContainer from '../../../Base/Components/ScrollContainer';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import FilledQuestionnaireFilter from './FilledQuestionnaireFilter';

export interface FilledQuestionnaireSelectedItem {
	id: string;
	questionnaireId: string;
}

const WRAPPER_ID = 'filledQuestionnaireListWrapper';

const FilledQuestionnaireList: React.FC = () => {
	const {
		getQuestionnaireResults,
		getNextPage,
		markAsRead,
		markAllAsRead,
		data,
		isLoading,
		page,
		setPage,
	} = useQuestionnaireResults();
	const [flatData, setFlatData] = useState<any[]>([]);
	const [hasMoreData, setHasMoreData] = useState<boolean>(false);
	const [filter, setFilter] = useState({});
	const [isAllChecked, setIsAllChecked] = useState(false);
	const [selectedItems, setSelectedItems] = useState<
		FilledQuestionnaireSelectedItem[]
	>([]);

	useEffect(() => {
		loadQuestionnaireResults();
	}, [page]);

	useEffect(() => {
		loadQuestionnaireResults(true);
	}, [filter]);

	useEffect(() => {
		if (data) {
			const questionnaireResultsData = data?.questionnaireResults?.data;
			const totalPages = data?.questionnaireResults?.meta?.totalPages;
			const page = data?.questionnaireResults?.meta?.page;

			if (questionnaireResultsData) {
				if (page && page > 1) {
					setFlatData(prevFlatData => [
						...prevFlatData,
						...questionnaireResultsData,
					]);
				} else {
					setFlatData(questionnaireResultsData);
				}
			}

			if (isNumber(totalPages) && isNumber(page)) {
				setHasMoreData(totalPages > page);
			}
		}
	}, [data]);

	const loadQuestionnaireResults = (reset: boolean = false) => {
		if (reset && page != 1) {
			setPage(1);
			return;
		}
		getQuestionnaireResults(filter);
	};

	const handleCheckAllItems = () => {
		setIsAllChecked(prev => !prev);
		if (isAllChecked) {
			setSelectedItems([]);
		}
	};

	const handleAddItemToSelected = (
		item: FilledQuestionnaireSelectedItem,
		add: boolean,
	) => {
		if (!item) {
			return null;
		}
		setSelectedItems(prev => {
			if (add) {
				return [...prev, item];
			}
			return prev.filter(({ id }) => id !== item.id);
		});
	};

	const handleMarkAsSeen = async () => {
		if (isAllChecked) {
			await markAllAsRead();
		} else {
			await markAsRead(selectedItems);
		}
		loadQuestionnaireResults(true);
	};

	return (
		<div className={styles.wrapper}>
			<FilledQuestionnaireFilter onFilter={filter => setFilter(filter)} />
			<div className={styles.options}>
				<Checkbox
					tx={'base.everything'}
					value={isAllChecked}
					onChange={handleCheckAllItems}
					firstUpper
				/>
				<div className={styles.seenWrapper}>
					<img src={'/dist/icons/eye.svg'} />
					<LinkButton
						title={'base.markAsSeen'}
						theme={Theme.BLUE}
						onClick={handleMarkAsSeen}
					/>
				</div>
			</div>
			<ScrollContainer wrapperId={WRAPPER_ID}>
				<FlatList
					onRefresh={loadQuestionnaireResults}
					onLoadMore={getNextPage}
					data={flatData}
					isLoading={isLoading}
					hasMoreData={hasMoreData}
					scrollableTargetId={WRAPPER_ID}
					renderItem={item => (
						<FilledQuestionnaireItem
							id={item.id}
							createdAt={item.createdAt}
							questionnaire={item.questionnaire.title}
							questionnaireId={item.questionnaire.id}
							patient={item.user}
							patientName={item.user?.profile?.fullName}
							patientId={item.user?.id}
							patientBirthNumber={item.user?.profile?.formattedBirthNumber}
							doctor={item.scheduledBy?.profile?.fullName}
							isSeen={item.isSeen}
							result={item.result}
							questionnaireCode={item?.questionnaire?.code}
							isSelected={isAllChecked}
							onSelect={handleAddItemToSelected}
							isSeenMarkedBy={item?.isSeenMarkedBy?.profile?.fullName}
							reloadList={loadQuestionnaireResults}
						/>
					)}
				/>
			</ScrollContainer>
		</div>
	);
};

export default FilledQuestionnaireList;
